import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { StatusPill, EditionPill } from "@/components/PackCard";
import { useAddToGlossLink, AddButton, AfterAddHelp, PlanNote } from "@/components/AddToGlossLink";
import { VerifiedBadge } from "@/components/VerifyPack";
import { api, errMsg, fmtDate } from "@/lib/api";

const Meta = ({ label, children, testId }) => (
  <div className="space-y-0.5">
    <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-800" data-testid={testId}>{children}</dd>
  </div>
);

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 break-all text-blue-700 hover:underline">{children}<ExternalLink className="h-3 w-3 shrink-0" /></a>
);

const EntryBrowser = ({ pack }) => {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: pack.entry_count, items: pack.sample_entries });
  useEffect(() => {
    if (!q.trim()) return setData({ total: pack.entry_count, items: pack.sample_entries });
    const t = setTimeout(() => api.get(`/packs/${pack.id}/entries`, { params: { q, limit: 20 } }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, pack]);
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="pack-preview-search-input" placeholder="Search terms in this pack" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>
      <p className="text-xs text-slate-500" data-testid="pack-preview-search-count">{q.trim() ? `${data.total} matching terms` : `Showing first ${data.items.length} of ${data.total} terms`}</p>
      <div className="space-y-3">
        {data.items.map((e) => (
          <div key={e.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="pack-preview-entry">
            <mark className="bg-yellow-200 border-b-2 border-yellow-500 rounded-sm px-1 font-semibold">{e.term}</mark>
            <p className="mt-2 whitespace-pre-line font-reading text-sm leading-relaxed text-slate-700">{e.definition}</p>
            <p className="mt-1 text-xs text-slate-500" data-testid="pack-preview-entry-source">— {pack.source_line}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const Body = ({ pack }) => {
  const { sent, busy, add } = useAddToGlossLink(pack);
  return (
    <div className="space-y-7">
      <DialogHeader className="space-y-3 text-left">
        <div className="flex flex-wrap gap-2">
          {pack.status !== "Published" && <StatusPill status={pack.status} testId="pack-preview-status" />}
          <EditionPill label={pack.edition_label} testId="pack-preview-edition-label" />
          <VerifiedBadge verification={pack.verification} testId="pack-preview-verified" />
        </div>
        <DialogTitle className="text-3xl font-extrabold leading-tight tracking-tight" data-testid="pack-preview-title">{pack.title}</DialogTitle>
        <DialogDescription className="text-base text-slate-600" data-testid="pack-preview-description">{pack.description || "No description provided."}</DialogDescription>
      </DialogHeader>

      <div className="space-y-3 rounded-2xl bg-blue-50 p-5">
        <AddButton pack={pack} sent={sent} busy={busy} add={add} size="lg" testId="pack-preview-add-button" />
        <PlanNote pack={pack} />
        <AfterAddHelp pack={pack} sent={sent} />
      </div>

      <dl className="grid grid-cols-1 gap-x-8 gap-y-4 rounded-xl border border-slate-200 p-5 sm:grid-cols-2">
        <Meta label="Subject" testId="pack-preview-subject">{pack.subject}</Meta>
        <Meta label="Source textbook" testId="pack-preview-textbook">{pack.textbook_title} — {pack.edition}</Meta>
        <Meta label="Authors / editors" testId="pack-preview-authors">{pack.authors}</Meta>
        <Meta label="Publisher" testId="pack-preview-publisher">{pack.publisher}</Meta>
        <Meta label="Source page" testId="pack-preview-source-url"><Ext href={pack.source_url}>{pack.source_url}</Ext></Meta>
        <Meta label="Terms" testId="pack-preview-entry-count">{pack.entry_count} terms, word for word</Meta>
        <Meta label="Pack version" testId="pack-preview-version">v{pack.version}</Meta>
        <Meta label="Imported" testId="pack-preview-date-imported">{fmtDate(pack.date_imported)}</Meta>
        <Meta label="Verified against source" testId="pack-preview-date-checked">{pack.verification?.verified ? `${fmtDate(pack.verification.checked_at)} — all ${pack.verification.total} entries match` : "Not yet verified"}</Meta>
      </dl>

      <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="pack-preview-access-terms">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-800">License &amp; attribution</p>
        <p className="text-sm text-slate-800">License: <Ext href={pack.license_url}>{pack.license_name}</Ext></p>
        <p className="mt-2 font-reading text-sm italic text-slate-800" data-testid="pack-preview-attribution">{pack.attribution_text}</p>
        <p className="mt-3 text-xs text-slate-600">Definitions are shown exactly as written in the source glossary. They are never rewritten or summarized. In GlossLink, each definition ends with "{pack.source_line}" so the credit travels with the term.</p>
      </section>

      {pack.other_editions.length > 0 && (
        <p className="text-sm text-slate-600" data-testid="pack-preview-other-editions">
          Other editions of this textbook: {pack.other_editions.map((p) => p.edition).join(", ")}. Keep only one edition in GlossLink so definitions don't clash.
        </p>
      )}

      <section>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Terms preview</p>
        <EntryBrowser pack={pack} />
      </section>
    </div>
  );
};

export const PackPreviewDialog = ({ packId, onOpenChange }) => {
  const [pack, setPack] = useState(null);
  useEffect(() => {
    setPack(null);
    if (packId) api.get(`/packs/${packId}`).then((r) => setPack(r.data)).catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
  }, [packId, onOpenChange]);

  return (
    <Dialog open={!!packId} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pack-preview-dialog" className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-3xl">
        {!pack ? <div className="py-16 text-center text-slate-500">Loading pack…</div> : <Body pack={pack} />}
      </DialogContent>
    </Dialog>
  );
};
